import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import type { MatchInterstitialContent } from '../matchFlow';

interface MatchInterstitialProps {
	content: MatchInterstitialContent;
	/** Quantas perguntas já foram respondidas / total, para o feedback numérico. */
	answered: number;
	total: number;
	onNext: () => void;
}

/** Tela curta de feedback entre blocos de perguntas. */
const MatchInterstitial = ({
	content,
	answered,
	total,
	onNext,
}: MatchInterstitialProps) => {
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				onNext();
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [onNext]);

	return (
		<div className="flex w-full flex-1 flex-col justify-center gap-8 py-6">
			<motion.span
				initial={{ scale: 0.6, opacity: 0, rotate: -12 }}
				animate={{ scale: 1, opacity: 1, rotate: 0 }}
				transition={{ type: 'spring', stiffness: 220, damping: 16 }}
				className="inline-flex h-14 w-14 items-center justify-center rounded-3xl bg-brand-pink text-white shadow-brand-pink"
			>
				<Sparkles className="h-6 w-6" />
			</motion.span>

			<div className="space-y-3">
				<motion.p
					initial={{ opacity: 0, y: 8 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.1 }}
					className="text-[11px] font-black uppercase tracking-[0.2em] text-brand-pink"
				>
					{content.eyebrow}
				</motion.p>
				<motion.h2
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.18 }}
					className="text-2xl font-black leading-tight tracking-tight text-stone-900 md:text-[2rem]"
				>
					{content.title}
				</motion.h2>
				<motion.p
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.26 }}
					className="max-w-xl text-base font-medium text-stone-500"
				>
					{content.body}
				</motion.p>
			</div>

			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 0.36 }}
				className="flex flex-wrap items-center justify-between gap-4"
			>
				<span className="text-xs font-bold uppercase tracking-[0.16em] text-stone-400">
					{answered} de {total} respondidas
				</span>
				<button
					type="button"
					onClick={onNext}
					className="inline-flex items-center gap-2 rounded-full bg-stone-900 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-stone-800"
				>
					Continuar
					<ArrowRight className="h-4 w-4" />
				</button>
			</motion.div>
		</div>
	);
};

export { MatchInterstitial };
